import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, Tag, ArrowLeft, User, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

function estimateReadTime(content: string) {
  const words = content.split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      const { data, error } = await supabase
        .from('BlogPost')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single();

      if (!error && data) {
        setPost(data);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (!post) return;

    const url = `https://vcapz.com/blog/${post.slug}`;
    const desc = post.excerpt || post.title;
    const image = post.imageUrl || "https://vcapz.com/og-image.png";

    // Page title
    document.title = `${post.title} | VCapz Media`;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr === "property" ? "property" : "name", sel.match(/["']([^"']+)["']/)?.[1] ?? "");
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    setMeta('meta[name="description"]',        "name",     desc);
    setMeta('meta[property="og:title"]',       "property", post.title);
    setMeta('meta[property="og:description"]', "property", desc);
    setMeta('meta[property="og:image"]',       "property", image);
    setMeta('meta[property="og:url"]',         "property", url);
    setMeta('meta[property="og:type"]',        "property", "article");
    setMeta('meta[name="twitter:title"]',      "name",     post.title);
    setMeta('meta[name="twitter:description"]',"name",     desc);
    setMeta('meta[name="twitter:image"]',      "name",     image);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // JSON-LD Article schema
    const ldId = "ld-blog-post";
    document.getElementById(ldId)?.remove();
    const ld = document.createElement("script");
    ld.id = ldId;
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": desc,
      "image": image,
      "url": url,
      "datePublished": post.publishedAt,
      "dateModified": post.updatedAt || post.publishedAt,
      "author": {
        "@type": "Organization",
        "@id": "https://vcapz.com/#organization",
        "name": post.author || "VCapz Media"
      },
      "publisher": {
        "@id": "https://vcapz.com/#organization"
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": url },
      "keywords": post.tags || ""
    });
    document.head.appendChild(ld);

    return () => {
      document.getElementById(ldId)?.remove();
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-28 flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background pt-28 flex flex-col items-center justify-center">
        <h1 className="text-4xl font-black text-white mb-6">Post Not Found</h1>
        <Link to="/blog" className="btn-primary flex items-center gap-2">
          <ArrowLeft size={16} /> Back to Blog
        </Link>
      </div>
    );
  }

  const tags = post.tags ? post.tags.split(",").map((t: string) => t.trim()).filter(Boolean) : [];
  const readTime = estimateReadTime(post.content);

  return (
    <div className="min-h-screen bg-background pt-28 pb-20">
      <article className="w-full max-w-3xl mx-auto px-4 sm:px-8">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10">
          <ArrowLeft size={16} /> Back to Blog
        </Link>

        {/* Hero image */}
        {post.imageUrl && (
          <div className="relative w-full aspect-[16/7] rounded-2xl overflow-hidden mb-10 border border-white/10">
            <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {tags.map((tag: string) => (
              <span key={tag} className="flex items-center gap-1 text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full">
                <Tag size={10} /> {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">{post.title}</h1>

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-text-muted mb-10 pb-10 border-b border-white/10">
          {post.author && (
            <span className="flex items-center gap-2"><User size={16} className="text-primary" />{post.author}</span>
          )}
          {post.publishedAt && (
            <span className="flex items-center gap-2">
              <Calendar size={16} className="text-primary" />
              {new Date(post.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </span>
          )}
          <span className="flex items-center gap-2"><Clock size={16} className="text-primary" />{readTime} min read</span>
        </div>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-xl text-text-muted leading-relaxed mb-10 italic border-l-4 border-primary pl-6">
            {post.excerpt}
          </p>
        )}

        {/* Content — rendered as prose */}
        <div
          className="prose-blog"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Footer */}
        <div className="mt-16 pt-10 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div>
            <p className="text-sm text-text-muted">Published by</p>
            <p className="font-bold text-white">{post.author || "VCapz Media"}</p>
          </div>
          <Link to="/blog" className="btn-primary flex items-center gap-2">
            <ArrowLeft size={16} /> More Articles
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
